import React, { useEffect, useState } from "react";
import { ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import Spinner from "utils/Spinner";
import { AfDateRangePicker } from "components/Input/AfDateRangePicker";
import { Button } from "@heroui/react";
import { Link, useNavigate } from "react-router-dom";
import { EyeIcon, TrashIcon, PlusIcon, MagnifyingGlassIcon, DocumentArrowDownIcon } from "@heroicons/react/24/outline";

const PurchaseOrderList = () => {
  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [vendorId, setVendorId] = useState<any>(null);
  const [dateRange, setDateRange] = useState<any>(null);
  const [searchText, setSearchText] = useState("");
  const [vendorOptions, setVendorOptions] = useState<any[]>([]);

  // Fetch vendors for filter dropdown
  useQuery(
    ["vendorListForPOFilter"],
    () => ListCommanWay("/admin/vendor/list", {}, { page: 1, paginate: 1000 }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          const opts = (res?.data?.data || []).map((v: any) => ({
            value: v.id,
            label: v.vendor_name || v.companyName || `Vendor #${v.id}`,
          }));
          setVendorOptions(opts);
        }
      },
    },
  );

  // Helper: resolve vendor name from vendorOptions
  const getVendorName = (vId: number) => {
    const v = vendorOptions.find((o: any) => o.value === vId);
    return v?.label || "-";
  };

  // Build query
  const buildQuery = () => {
    const q: any = {};
    if (vendorId) q.vendorId = vendorId.value;
    if (searchText) q.search = searchText;
    if (dateRange?.start && dateRange?.end) {
      q.poDate = {
        $gte: dateRange.start,
        $lte: moment(dateRange.end).endOf("day"),
      };
    }
    return q;
  };

  // Fetch POs — only on Search button click or page change
  const {
    data: poData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    ["purchaseOrderList", currentPage],
    () =>
      ListCommanWay(
        "/admin/purchase-order/list",
        buildQuery(),
        { page: currentPage, paginate: perPage, sort: { createdAt: -1 } },
      ),
    {
      enabled: false,
      keepPreviousData: true,
    },
  );

  const orders =
    poData?.status === "SUCCESS" ? poData?.data?.docs || poData?.data?.data || poData?.data?.rows || [] : [];
  const totalPages = poData?.data?.pages || poData?.data?.paginator?.pageCount || 1;
  const totalCount = poData?.data?.total || poData?.data?.paginator?.itemCount || 0;

  // Soft delete mutation
  const { mutate: softDelete, isLoading: deleting } = useMutation(
    (id: number) => UpdateCommanWay({ url: `/admin/purchase-order/softDelete/${id}`, data: {} }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          UseToast("Purchase Order deleted", "success");
          refetch();
        } else {
          UseToast(res?.message || "Failed to delete", "error");
        }
      },
      onError: (err: any) => {
        UseToast(typeof err === "string" ? err : err?.message || "Error deleting", "error");
      },
    },
  );

  const handleDelete = (id: number) => {
    Swal.fire({
      title: "Delete Purchase Order?",
      text: "This will soft-delete the PO. Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    }).then((result) => {
      if (result.isConfirmed) {
        softDelete(id);
      }
    });
  };

  const handleDownloadPdf = (poNo?: string) => {
    if (!poNo) return UseToast("PO number not available", "error");
    window.open(`${BASE_URL}/purchase_orders/${poNo}.pdf`, "_blank");
  };

  // Initial load
  useEffect(() => {
    refetch();
  }, [currentPage]);

  const handleSearch = () => {
    if (currentPage === 1) {
      refetch();
    } else {
      setCurrentPage(1); // will trigger refetch via useEffect
    }
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">Purchase Orders</h1>
        <Button color="primary" onPress={() => navigate("/purchase-order/create")} startContent={<PlusIcon className="w-4 h-4" />}>
          New PO
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="w-56">
          <label className="text-sm font-medium mb-1 block">Vendor</label>
          <Select
            options={vendorOptions}
            value={vendorId}
            onChange={(val: any) => setVendorId(val)}
            isClearable
            placeholder="All Vendors"
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">PO Date</label>
          <AfDateRangePicker
            setStartDate={(d: any) => setDateRange((prev: any) => ({ ...prev, start: d }))}
            setEndDate={(d: any) => setDateRange((prev: any) => ({ ...prev, end: d }))}
          />
        </div>
        <div className="w-48">
          <label className="text-sm font-medium mb-1 block">Search</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-md px-3 py-[6px] text-sm"
            placeholder="PO No / Vendor..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div className="flex items-end">
          <Button
            color="primary"
            onPress={handleSearch}
            isLoading={isFetching}
            startContent={<MagnifyingGlassIcon className="w-4 h-4" />}
          >
            Search
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {["#", "PO Number", "Vendor", "PO Date", "Total Amt", "Actions"].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {orders.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                  {isLoading ? "Loading..." : "No purchase orders found"}
                </td>
              </tr>
            ) : (
              orders.map((po: any, idx: number) => (
                <tr key={po.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-sm">{(currentPage - 1) * perPage + idx + 1}</td>
                  <td className="px-3 py-2 text-sm font-medium">
                    <Link to={`/purchase-order/${po.id}`} className="text-blue-600 hover:underline">
                      {po.poNo || "-"}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-sm">{getVendorName(po.vendorId)}</td>
                  <td className="px-3 py-2 text-sm">{po.poDate ? moment(po.poDate).format("DD MMM YYYY") : "-"}</td>
                  <td className="px-3 py-2 text-sm font-medium">₹{po.grandTotal?.toLocaleString("en-IN") || "0"}</td>
                  <td className="px-3 py-2 text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/purchase-order/${po.id}`)}
                        className="text-blue-600 hover:text-blue-800"
                        title="View"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDownloadPdf(po.poNo)}
                        className="text-green-600 hover:text-green-800"
                        title="Download PDF"
                      >
                        <DocumentArrowDownIcon className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(po.id)} className="text-red-500 hover:text-red-700" title="Delete">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-600">
            Page {currentPage} of {totalPages} ({totalCount} total)
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="bordered" isDisabled={currentPage <= 1} onPress={() => setCurrentPage((p) => p - 1)}>
              Previous
            </Button>
            <Button size="sm" variant="bordered" isDisabled={currentPage >= totalPages} onPress={() => setCurrentPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {isFetching && !isLoading && (
        <div className="fixed top-4 right-4 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs">Refreshing...</div>
      )}
    </div>
  );
};

export default PurchaseOrderList;
